import { TFunction } from "i18next";
import { TaxonomyState } from "../store/taxonomy/taxonomy";
import { ExpandedColumn } from "../types/columns";
import { ClientLocation, RecommendationAzure, Report } from "../types/types";
import { formatNumber } from "./number";
import { formatDateString } from "./utils";

export const transformRecommendationsForExcel = ({
  recommendations,
  columns,
  t,
  taxonomy,
}: {
  recommendations: RecommendationAzure[];
  columns: ExpandedColumn[];
  t: TFunction;
  taxonomy: TaxonomyState;
}) => {
  return recommendations.map((v) => {
    return columns
      .filter((c) => c.isVisible)
      .reduce((transformed, c) => {
        if (c.excel && c.excel.render) {
          transformed[c.key] = c.excel.render({
            t,
            taxonomy,
            recommendation: v,
          });
        }
        //
        else {
          transformed[c.key] = v[c.key];
        }
        return transformed;
      }, {});
  });
};

export const transformLocationsForExcel = ({
  locations,
  t,
  taxonomy,
}: {
  locations: ClientLocation[];
  t: TFunction;
  taxonomy: TaxonomyState;
}) => {
  return locations.map((loc) => ({
    locationName: loc.locationName,
    clientName: loc.clientName,
    webBasePartnerNumber: loc.webBasePartnerNumber,
    siccode: taxonomy.SicCode.byId[loc.siccode]?.code
      ? t(taxonomy.SicCode.byId[loc.siccode].code)
      : loc.siccode,
    countryCode: taxonomy.Country.byId[loc.countryCode]?.code
      ? t(taxonomy.Country.byId[loc.countryCode].code)
      : loc.countryCode,
    postalCode: loc.postalCode,
    city: loc.city,
    turnoverInMioEur:
      loc.turnoverInMioEur != null ? formatNumber(loc.turnoverInMioEur) : "",
    tsiinMioEur: loc.tsiinMioEur != null ? formatNumber(loc.tsiinMioEur) : "",
    pmlinMioEur: loc.pmlinMioEur != null ? formatNumber(loc.pmlinMioEur) : "",
    emlinMioEur: loc.emlinMioEur != null ? formatNumber(loc.emlinMioEur) : "",
    ebitinMioEur:
      loc.ebitinMioEur != null ? formatNumber(loc.ebitinMioEur) : "",
    ygpcminMioEur:
      loc.ygpcminMioEur != null ? formatNumber(loc.ygpcminMioEur) : "",
    deductiblesInMioEur:
      loc.deductiblesInMioEur != null
        ? formatNumber(loc.deductiblesInMioEur)
        : "",
    coords: loc.latitude ? `${loc.latitude}, ${loc.longitude}` : "",
    insurersRaiting: loc.insurersRaiting || "",
  }));
};

export const transformReportsForExcel = ({
  reports,
  t,
  taxonomy,
}: {
  reports: Report[];
  t: TFunction;
  taxonomy: TaxonomyState;
}) => {
  return reports.map((report) => ({
    reportName: report.reportName,
    clientName: report.clientName,
    webBasePartnerNumber: report.webBasePartnerNumber,
    locationName: report.locationName,
    surveyDate: report.surveyDate ? formatDateString(report.surveyDate) : "",
    pmlinMioEur:
      report.pmlinMioEur != null ? formatNumber(report.pmlinMioEur) : "",
    emlinMioEur:
      report.emlinMioEur != null ? formatNumber(report.emlinMioEur) : "",
    insurersRaiting: report.insurersRaiting || "",
    reportCreatorCode: taxonomy.RiskReportCreator.byId[report.reportCreatorCode]
      ?.code
      ? t(taxonomy.RiskReportCreator.byId[report.reportCreatorCode].code)
      : report.reportCreatorCode,
    isReportDeactivated: report.isReportDeactivated
      ? t("greco.no")
      : t("greco.yes"),
  }));
};

export const transformClientsForExcel = ({
  clients,
  t,
  taxonomy,
}: {
  clients: any[];
  t: TFunction;
  taxonomy: TaxonomyState;
}) => {
  return clients.map((client) => ({
    clientName: client.clientName,
    clientId: client.clientId,
    countryCodeID: taxonomy.Country.byId[client.countryCodeID]?.code
      ? t(taxonomy.Country.byId[client.countryCodeID].code)
      : client.countryCodeID,
    costCenterCodeID: taxonomy.CostCenter.byId[client.costCenterCodeID]?.code
      ? t(taxonomy.CostCenter.byId[client.costCenterCodeID].code)
      : client.costCenterCodeID,
    goodRangeMin: client.goodRangeMin,
    moderateRangeMin: client.moderateRangeMin,
    poorRangeMin: client.poorRangeMin,
    highCostEstimateMin: client.highCostEstimateMin,
    mediumCostEstimateMin: client.mediumCostEstimateMin,
    lowCostEstimateMin: client.lowCostEstimateMin,
  }));
};
